/**
 * Betrieb Müller - Farm Profile
 *
 * A realistic mock farm scenario for demonstrating
 * Control Assistant and Document Tracking features.
 *
 * Location: Rotenburg (Wümme), Niedersachsen
 * Type: Mixed operation (crop + pig fattening)
 */

/**
 * Farm profile information
 */
export const FARM_PROFILE = {
  id: 'betrieb-mueller',
  name: 'Betrieb Müller - Musterhof',
  owner: {
    name: 'Klaus Müller',
    title: 'Betriebsinhaber',
  },
  address: {
    street: 'Hofweg 12',
    postalCode: '27356',
    city: 'Rotenburg (Wümme)',
    state: 'Niedersachsen',
    country: 'Deutschland',
  },
  // Official identifiers
  identifiers: {
    betriebsnummer: '034012345678',
    hiTierBetriebsnummer: 'DE 034 0123456',
    bnk: '034012345678901', // Betriebsnummer Konditionalität
  },
  // Farm characteristics
  characteristics: {
    farmType: 'Gemischtbetrieb (Ackerbau + Schweinemast)',
    totalArea: 85, // hectares
    arableArea: 75, // hectares
    grasslandArea: 10, // hectares
    fieldCount: 12,
    livestockUnits: 160, // Großvieheinheiten (GV)
    employees: 2,
    organicCertified: false,
    redZone: false, // Nicht im roten Gebiet (Nitratbelastung)
  },
  // Regulatory situation
  regulatoryStatus: {
    // DüV obligations
    duevRequired: true,
    stoffstrombilanzRequired: true, // >50 GV
    // Last controls
    lastDuevControl: new Date('2023-03-15'),
    lastCrossComplianceControl: new Date('2022-08-20'),
    // Next expected control period
    expectedControlPeriod: 'Q1-Q2 2026',
  },
  // Contact
  contact: {
    phone: '+49 4261 123456',
    email: 'klaus.mueller@musterhof.de',
  },
} as const;

/**
 * Farm characteristics for control calculations
 */
export const FARM_CONTROL_CONTEXT = {
  // DÜV control factors
  duev: {
    livestockDensity: 1.88, // GV/ha (160 GV / 85 ha)
    averageControlCycle: 3, // years in Niedersachsen
    lastControlDate: new Date('2023-03-15'),
    controlLikelihood: 'mittel' as const, // 3 years since last control
    requiredDocuments: 8,
    presentDocuments: 6,
    missingDocuments: 2,
  },
  // Stoffstrombilanz factors
  stoffstrom: {
    required: true, // >50 GV
    lastBilanzYear: 2024,
    currentBilanzStatus: 'pending', // 2025 not yet created
    nSaldo3YearAvg: 145, // kg N/ha (under 175 limit)
    pSaldo6YearAvg: 8, // kg P/ha (under 10 limit)
  },
  // Plant protection
  pflanzenschutz: {
    sachkundeHolders: 2, // Klaus + 1 employee
    sprayerLastCheck: new Date('2023-05-10'),
    sprayerNextCheck: new Date('2026-05-10'),
  },
} as const;

/**
 * Current date reference for mock data
 * All mock data dates are relative to this
 */
export const MOCK_CURRENT_DATE = new Date('2026-01-26');

/**
 * Helper to create dates relative to the mock current date
 */
export function daysFromNow(days: number): Date {
  const date = new Date(MOCK_CURRENT_DATE);
  date.setDate(date.getDate() + days);
  return date;
}

export function daysAgo(days: number): Date {
  return daysFromNow(-days);
}

export function hoursAgo(hours: number): Date {
  const date = new Date(MOCK_CURRENT_DATE);
  date.setHours(date.getHours() - hours);
  return date;
}

export function monthsFromNow(months: number): Date {
  const date = new Date(MOCK_CURRENT_DATE);
  date.setMonth(date.getMonth() + months);
  return date;
}

export function yearsAgo(years: number): Date {
  const date = new Date(MOCK_CURRENT_DATE);
  date.setFullYear(date.getFullYear() - years);
  return date;
}
